import Button from "@imminently/decisively-ui/Button";
import DropdownMenu from "@imminently/decisively-ui/DropdownMenu";
import Chip, { type ChipVariantKey } from "@imminently/decisively-ui/Chip";

import Icon from "@imminently/decisively-ui/Icon";
import { useScope } from '@/redux/scope';
import { useBrowseReleasesModal, useReleaseModal, ReleaseChangeHistoryButton, getReleaseEnvLabel, getReleaseUiName, useCurrentReleases, useRecentReleases, useViewRelease } from '@/features/release';
import { useTranslation } from 'react-i18next';
import { useFullRelease } from '@/common/hooks_useFullRelease';
import { Skeleton } from '@/ui/primitives';
import { useRelease as useCachedRelease, useProject } from '@/common/hooks';
import { isFeatureEnabled } from "@/config/features";
import Select from "@imminently/decisively-ui/Select";

const ENV_VARIANT: Record<string, ChipVariantKey> = {
  prod: "success",
  test: "subtle",
};

export type ReleaseSelectorBaseProps = {
  allowAny?: boolean;
  releaseId?: string | null;
  onReleaseChange?: (releaseId: string | null) => void;
};

export const ReleaseSelector = ({
  allowAny = false,
  releaseId: controlledReleaseId,
  onReleaseChange,
}: ReleaseSelectorBaseProps = {}) => {
  const { t } = useTranslation();
  const openBrowseReleases = useBrowseReleasesModal();
  const openRelease = useReleaseModal();
  const viewRelease = useViewRelease();
  const { project: projectId, release: scopeReleaseId } = useScope();
  const releaseId = controlledReleaseId === undefined ? scopeReleaseId : controlledReleaseId;
  const project = useProject(projectId);
  const fullRelease = useFullRelease();
  const cachedRelease = useCachedRelease(releaseId);
  const release = releaseId === scopeReleaseId ? (fullRelease ?? cachedRelease) : cachedRelease;
  const { releases: currentReleases } = useCurrentReleases(project?.id);
  const { releases: recentReleases } = useRecentReleases(project?.id);

  if (!project || (!release && !(allowAny && !releaseId))) return <Skeleton className="w-60 h-6" />;

  // calculate after we know we have the object
  const releaseName = release ? getReleaseUiName(release) : "All";
  const isCompact = isFeatureEnabled('design.compact');
  const selectRelease = (nextReleaseId: string | null) => {
    if (onReleaseChange) {
      onReleaseChange(nextReleaseId);
      return;
    }
    if (nextReleaseId) viewRelease(nextReleaseId);
  };

  return (
    <DropdownMenu>
      <DropdownMenu.Trigger asChild>
        {isCompact ? (
          <Button label={releaseName} variant="ghost" size="sm" className="gap-2">
            <Icon name="package" size={16} />
            <span className="flex-1 text-start">{releaseName}</span>
            {release ? (
              <Chip
                variant={ENV_VARIANT[release.env ?? 'test']}
                size="sm"
                label={getReleaseEnvLabel(release.env)}
              />
            ) : null}
            <Icon name="resize" size={16} className="text-muted-foreground shrink-0" />
          </Button>
        ) : (
          <div className="w-60">
            <Select
              value={releaseName}
              options={releaseName ? [{ value: String(releaseName), label: String(releaseName) }] : []}
              placeholder={t("releases_label")}
              searchable={false}
              menuIsOpen={false}
              openMenuOnClick={false}
              openMenuOnFocus={false}
              onValueChange={() => { }}
              className="w-full text-left"
            />
          </div>
        )}
      </DropdownMenu.Trigger>
      <DropdownMenu.Content align="start" className="min-w-60">
        {allowAny ? (
          <DropdownMenu.Group>
            <DropdownMenu.Item
              onClick={() => selectRelease(null)}
              data-selected={!releaseId ? "true" : undefined}
            >
              <DropdownMenu.Item.Label className="text-sm font-normal">All</DropdownMenu.Item.Label>
            </DropdownMenu.Item>
          </DropdownMenu.Group>
        ) : null}
        {allowAny && currentReleases.length > 0 ? <DropdownMenu.Separator /> : null}
        {currentReleases.length > 0 && (
          <DropdownMenu.Group>
            <DropdownMenu.Label className="font-semibold uppercase">{t('current')}</DropdownMenu.Label>
            {currentReleases.map((r) => (
              <DropdownMenu.Item
                key={r.id}
                onClick={() => selectRelease(r.id)}
                data-selected={r.id === releaseId ? "true" : undefined}
              >
                <DropdownMenu.Item.Label className="text-sm font-normal">{getReleaseUiName(r)}</DropdownMenu.Item.Label>
                <Chip
                  variant={ENV_VARIANT[r.env ?? 'test']}
                  size="sm"
                  label={getReleaseEnvLabel(r.env)}
                />
              </DropdownMenu.Item>
            ))}
          </DropdownMenu.Group>
        )}
        {currentReleases.length > 0 && recentReleases.length > 0 && <DropdownMenu.Separator />}
        {recentReleases.length > 0 && (
          <DropdownMenu.Group>
            <DropdownMenu.Label className="font-semibold uppercase">{t('recent')}</DropdownMenu.Label>
            {recentReleases.map((r) => (
              <DropdownMenu.Item
                key={r.id}
                onClick={() => selectRelease(r.id)}
                data-selected={r.id === releaseId ? "true" : undefined}
              >
                <DropdownMenu.Item.Label className="text-sm font-normal">{getReleaseUiName(r)}</DropdownMenu.Item.Label>
                <Chip
                  variant={ENV_VARIANT[r.env ?? 'test']}
                  size="sm"
                  label={getReleaseEnvLabel(r.env)}
                />
              </DropdownMenu.Item>
            ))}
          </DropdownMenu.Group>
        )}
        <DropdownMenu.Separator />
        <div className="flex flex-row gap-1 justify-between p-1">
          <DropdownMenu.Item asChild>
            <Button
              className="shrink"
              variant="ghost"
              size="sm"
              onClick={() => release && openRelease(release)}
              disabled={!release}
              label={t('release_centre.new_release')}
            >
              <Icon name="add" />
              {t('release_centre.new_release')}
            </Button>
          </DropdownMenu.Item>
          <DropdownMenu.Item asChild>
            <Button
              className="shrink"
              variant="ghost"
              size="sm"
              onClick={() => openBrowseReleases(project.id)}
              label={t('browse_all')}
            >
              <Icon name="browse-all" />
              {t('browse_all')}
            </Button>
          </DropdownMenu.Item>
        </div>
      </DropdownMenu.Content>
    </DropdownMenu>
  );
}
